import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useState } from 'react';

/**
 * takes in a source of truth for some value and a change function
 * @returns the value and a Dispatch which allows the source of truth to be temporarily updated
 * until it changes again as determined by the change function
 */
export const useLocalOptimism = <T extends unknown>(
  /** incoming source of truth. this will be the value after any new change as determined by the `changed` fn */
  sourceOfTruth: T,
  /** comparator function to determine if the source of truth has changed. `!==` by default */
  hasChanged: (prev: T, next: T) => boolean = referentiallyChanged
) => {
  const [prevTruth, setPrevTruth] = useState<T>(sourceOfTruth);
  const [optimisticValue, setOptimisticValue] = useState<T>(sourceOfTruth);

  const setOptimisticValueCallback = useCallback<Dispatch<SetStateAction<T>>>(
    (action) => {
      if (typeof action === 'function') {
        setOptimisticValue((action as (prevState: T) => T)(sourceOfTruth));
      } else {
        setOptimisticValue(action);
      }
    },
    [sourceOfTruth]
  );

  let valueToReturn = optimisticValue;

  // source of truth value passed into the hook has changed
  if (hasChanged(prevTruth, sourceOfTruth)) {
    setOptimisticValue(sourceOfTruth);
    setPrevTruth(sourceOfTruth);
    valueToReturn = sourceOfTruth;
  }

  return [valueToReturn, setOptimisticValueCallback] as const;
};

const referentiallyChanged = <T extends any>(prev: T, next: T) => prev !== next;
